import React, { useEffect, useState, useMemo } from "react";
import { Modal, Table, Tag, Segmented, Spin, Empty, Button, Drawer, Tooltip } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { 
  Mail, 
  ShieldCheck, 
  Bell, 
  Eye, 
  Calendar, 
  Building2, 
  User, 
  CheckCircle2, 
  Clock, 
  RefreshCw,
  ExternalLink
} from "lucide-react";
import { getAdminUserEmails } from "../../utils/users";
import dayjs from "dayjs";

const UserEmailsModal = ({
  open,
  onClose,
  user,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'admin' | 'normal'
  const [adminEmails, setAdminEmails] = useState([]);
  const [normalEmails, setNormalEmails] = useState([]);
  const [counts, setCounts] = useState({ admin: 0, normal: 0, total: 0 });
  const [previewEmail, setPreviewEmail] = useState(null);

  const fetchEmails = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const token = await getIdTokenClaims();
      const res = await getAdminUserEmails(token.__raw, user.id);
      if (res.data?.success) {
        setAdminEmails(res.data.admin_emails || []);
        setNormalEmails(res.data.normal_emails || []);
        setCounts(res.data.counts || { admin: 0, normal: 0, total: 0 });
      }
    } catch (err) {
      console.error("Error fetching user emails:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && user?.id) {
      setActiveTab("all");
      setPreviewEmail(null);
      fetchEmails();
    } else {
      setAdminEmails([]);
      setNormalEmails([]);
      setCounts({ admin: 0, normal: 0, total: 0 });
      setPreviewEmail(null);
    }
  }, [open, user?.id]);

  // Combine and format data with bifurcation tags
  const combinedList = useMemo(() => {
    const formattedAdmin = adminEmails.map((item) => ({
      ...item,
      key: `admin_${item.id}`,
      bifurcation: "admin",
      displayType: item.email_type ? item.email_type.replace(/_/g, " ").toUpperCase() : "ADMIN EMAIL",
      displayDate: item.created_at,
    }));

    const formattedNormal = normalEmails.map((item) => ({
      ...item,
      key: `normal_${item.id}`,
      bifurcation: "normal",
      displayType: item.email_type ? item.email_type.replace(/_/g, " ").toUpperCase() : "SYSTEM NOTIFICATION",
      displayDate: item.created_at,
    }));

    if (activeTab === "admin") {
      return formattedAdmin;
    }
    if (activeTab === "normal") {
      return formattedNormal;
    }

    // "all": Merge and sort descending by created_at / id
    return [...formattedAdmin, ...formattedNormal].sort((a, b) => {
      const timeA = new Date(a.displayDate || a.created_at).getTime();
      const timeB = new Date(b.displayDate || b.created_at).getTime();
      return timeB - timeA;
    });
  }, [adminEmails, normalEmails, activeTab]);

  const columns = [
    {
      title: "Date & Time",
      dataIndex: "created_at",
      key: "created_at",
      width: 170,
      render: (date) => (
        <div className="flex flex-col">
          <span className="text-xs font-medium text-gray-800 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            {date ? dayjs(date).format("DD MMM YYYY") : "—"}
          </span>
          <span className="text-[11px] text-gray-400 ml-4.5">
            {date ? dayjs(date).format("hh:mm A") : ""}
          </span>
        </div>
      ),
    },
    {
      title: "Bifurcation Source",
      dataIndex: "bifurcation",
      key: "bifurcation",
      width: 220,
      render: (source, record) => {
        if (source === "admin") {
          return (
            <div className="flex flex-col gap-1">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 w-max">
                <ShieldCheck className="w-3.5 h-3.5 text-purple-600" />
                Admin Panel
              </span>
              <span className="text-[11px] text-gray-500 font-medium">
                Sent by Admin ID: <strong className="text-gray-700">#{record.admin_id}</strong>
                {record.admin_name ? ` (${record.admin_name})` : record.admin_email ? ` (${record.admin_email})` : ""}
              </span>
            </div>
          );
        }

        return (
          <div className="flex flex-col gap-1">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 w-max">
              <Bell className="w-3.5 h-3.5 text-emerald-600" />
              Normal / System
            </span>
            <span className="text-[11px] text-gray-400">Automated Notification</span>
          </div>
        );
      },
    },
    {
      title: "Type & Details",
      key: "type_details",
      width: 210,
      render: (_, record) => (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-gray-700 tracking-wide">
            {record.displayType}
          </span>
          {record.organization_name && (
            <span className="text-[11px] text-blue-600 flex items-center gap-1 truncate max-w-xs" title={record.organization_name}>
              <Building2 className="w-3 h-3 flex-shrink-0" />
              {record.organization_name}
              {record.provider && (
                <Tag color={record.provider === "qbo" ? "green" : "cyan"} className="text-[10px] px-1 py-0 leading-tight uppercase font-medium">
                  {record.provider}
                </Tag>
              )}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      ellipsis: true,
      render: (subject) => (
        <div className="text-xs text-gray-900 font-medium truncate max-w-md" title={subject}>
          {subject || "—"}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      align: "center",
      render: (_, record) => {
        const isSent = record.status === "sent" || record.is_sent == 1;
        return isSent ? (
          <Tag color="success" className="text-xs font-medium inline-flex items-center gap-1 px-2 py-0.5 rounded-full">
            <CheckCircle2 className="w-3 h-3" /> Sent
          </Tag>
        ) : (
          <Tag color="warning" className="text-xs font-medium inline-flex items-center gap-1 px-2 py-0.5 rounded-full">
            <Clock className="w-3 h-3" /> Pending
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 110,
      align: "center",
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => setPreviewEmail(record)}
          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium cursor-pointer"
        >
          <Eye className="w-3.5 h-3.5" />
          View Email
        </Button>
      ),
    },
  ];

  const displayName = user?.name || user?.email || "User";

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        width={1050}
        footer={null}
        destroyOnClose
        centered
        title={
          <div className="flex items-center justify-between pr-8 border-b border-gray-100 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-lg border border-blue-100">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-gray-900 m-0 leading-tight">
                    Emails History
                  </h2>
                  <Tag color="blue" className="font-medium text-xs">
                    {counts.total} Total
                  </Tag>
                </div>
                <p className="text-xs text-gray-500 m-0 mt-0.5 flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-gray-400" />
                  <span className="font-medium text-gray-700">{displayName}</span>
                  {user?.email && (
                    <span className="text-gray-400">({user.email})</span>
                  )}
                  {user?.alternate_email && (
                    <span className="text-blue-600 font-normal">
                      • Alt: {user.alternate_email}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <Button
              size="small"
              icon={<RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />}
              onClick={fetchEmails}
              loading={loading}
              className="text-xs flex items-center gap-1.5"
            >
              Refresh
            </Button>
          </div>
        }
      >
        <div className="py-3">
          {/* Bifurcation Tabs */}
          <div className="flex items-center justify-between mb-4 bg-gray-50 p-1.5 rounded-xl border border-gray-200">
            <Segmented
              value={activeTab}
              onChange={setActiveTab}
              className="user-email-bifurcation-tabs"
              options={[
                {
                  label: (
                    <div className="flex items-center gap-2 px-2 py-1 text-xs font-semibold">
                      <Mail className="w-3.5 h-3.5 text-gray-500" />
                      <span>All Emails</span>
                      <span className="bg-gray-200 text-gray-700 text-[11px] px-1.5 py-0.2 rounded-full font-bold">
                        {counts.total}
                      </span>
                    </div>
                  ),
                  value: "all",
                },
                {
                  label: (
                    <div className="flex items-center gap-2 px-2 py-1 text-xs font-semibold">
                      <ShieldCheck className="w-3.5 h-3.5 text-purple-600" />
                      <span>Admin Panel Emails</span>
                      <span className="bg-purple-100 text-purple-800 text-[11px] px-1.5 py-0.2 rounded-full font-bold">
                        {counts.admin}
                      </span>
                    </div>
                  ),
                  value: "admin",
                },
                {
                  label: (
                    <div className="flex items-center gap-2 px-2 py-1 text-xs font-semibold">
                      <Bell className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Normal / System Emails</span>
                      <span className="bg-emerald-100 text-emerald-800 text-[11px] px-1.5 py-0.2 rounded-full font-bold">
                        {counts.normal}
                      </span>
                    </div>
                  ),
                  value: "normal",
                },
              ]}
            />
            <div className="text-xs text-gray-500 pr-2">
              Showing <strong className="text-gray-800">{combinedList.length}</strong> records
            </div>
          </div>

          {/* Email Table */}
          <Table
            columns={columns}
            dataSource={combinedList}
            loading={loading}
            pagination={{
              pageSize: 8,
              showSizeChanger: true,
              pageSizeOptions: ["8", "15", "30", "50"],
              showTotal: (total) => `Total ${total} emails`,
              size: "small",
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span className="text-xs text-gray-500">
                      No emails found in the <strong>{activeTab.toUpperCase()}</strong> category for this user.
                    </span>
                  }
                />
              ),
            }}
            size="middle"
            className="border border-gray-100 rounded-lg overflow-hidden shadow-sm"
          />
        </div>
      </Modal>

      {/* Email Body Preview Drawer */}
      <Drawer
        title={
          <div className="flex items-center justify-between pr-4">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              <div>
                <h3 className="text-sm font-bold text-gray-900 m-0">Email Content Preview</h3>
                <p className="text-[11px] text-gray-500 m-0">
                  {previewEmail?.displayType} • {previewEmail?.displayDate ? dayjs(previewEmail.displayDate).format("DD MMM YYYY, hh:mm A") : ""}
                </p>
              </div>
            </div>
            {previewEmail?.bifurcation === "admin" ? (
              <Tag color="purple" className="font-semibold text-xs inline-flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Admin Panel
              </Tag>
            ) : (
              <Tag color="green" className="font-medium text-xs inline-flex items-center gap-1">
                <Bell className="w-3 h-3" /> Normal / System
              </Tag>
            )}
          </div>
        }
        placement="right"
        width={650}
        onClose={() => setPreviewEmail(null)}
        open={Boolean(previewEmail)}
        destroyOnClose
      >
        {previewEmail && (
          <div className="flex flex-col h-full gap-4">
            {/* Metadata Card */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs space-y-2">
              <div className="flex">
                <span className="w-24 text-gray-400 font-medium">Subject:</span>
                <span className="text-gray-900 font-bold flex-1">{previewEmail.subject || "No Subject"}</span>
              </div>
              <div className="flex">
                <span className="w-24 text-gray-400 font-medium">Recipient:</span>
                <span className="text-gray-700 font-medium flex-1">
                  {previewEmail.recipient_email || user?.email}
                </span>
              </div>
              {previewEmail.bifurcation === "admin" && (
                <div className="flex">
                  <span className="w-24 text-purple-600 font-medium">Sent by:</span>
                  <span className="text-gray-800 font-semibold flex-1">
                    Admin #{previewEmail.admin_id}
                    {previewEmail.admin_name ? ` (${previewEmail.admin_name})` : ""}
                    {previewEmail.admin_email ? ` <${previewEmail.admin_email}>` : ""}
                  </span>
                </div>
              )}
              {previewEmail.organization_name && (
                <div className="flex">
                  <span className="w-24 text-gray-400 font-medium">Organization:</span>
                  <span className="text-blue-700 font-medium flex-1">
                    {previewEmail.organization_name} ({previewEmail.provider?.toUpperCase()})
                  </span>
                </div>
              )}
              <div className="flex">
                <span className="w-24 text-gray-400 font-medium">Sent At:</span>
                <span className="text-gray-700 flex-1">
                  {previewEmail.created_at ? dayjs(previewEmail.created_at).format("YYYY-MM-DD HH:mm:ss") : "—"}
                </span>
              </div>
            </div>

            {/* Rendered HTML Email Body */}
            <div className="flex-1 border border-gray-200 rounded-lg p-4 bg-white overflow-auto shadow-inner">
              <div
                className="email-body-content prose max-w-none text-sm text-gray-800"
                dangerouslySetInnerHTML={{ __html: previewEmail.body || previewEmail.message || "<p className='text-gray-400 italic'>No body content available</p>" }}
              />
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
};

export default UserEmailsModal;
